'use client';

/**
 * ContentAnswerCard — one authored answer (thin, registered for capture).
 *
 * Renders the node's mirrored blocks STRUCTURALLY: prose as plain
 * paragraphs, `TableBlock` as a real `<table>`, `ListBlock` as a real
 * list. No markdown parsing — the structured block model is the whole
 * point. Mounted by the per-layout collection cards (faq grid / comparison
 * / letter); not preset-placed itself (its count is data-driven).
 *
 * @see klyde_edge/ui_config/composition/cards/investor_relations.py — content_key="content_answer"
 */

import { useMemo } from 'react';
import { useCardExport } from '@/lib/design/card-export';
import { cn } from '@/lib/utils/cn';
import { text } from '@/lib/design/tokens';
import type { ContentBlock, ContentQuestion } from './use-content-set';

export function ContentAnswerCard({
  entry,
  queryKey,
}: {
  entry: ContentQuestion;
  queryKey: readonly unknown[];
}) {
  // Blocks can be absent on a resolved entry — e.g. an Ask-compose lane match
  // whose node has no mirrored body — so guard rather than crash on `.map`.
  // A block-less answer still renders its title.
  const blocks = entry.blocks ?? [];
  // Scene export ships the PAINTED content, not just metadata (capture
  // rule 3): the card renders entry.blocks, so the export carries the same
  // text/tables/lists the user read — text (plain, emphasis stripped to
  // mirror the screen), markdown (richer paste target), and json blocks in
  // painted form. Without this the scene is hollow and render-correctness
  // can't be checked against the payload.
  useCardExport(useMemo(() => ({
    label: entry.title,
    content_key: 'content_answer',
    status: 'loaded' as const,
    query_key: queryKey,
    text: `${entry.title}\n\n${blocks.map(blockToText).join('\n\n')}`,
    markdown: `### ${entry.title}\n\n${blocks.map(blockToMarkdown).join('\n\n')}`,
    json: {
      key: entry.key, title: entry.title, scope: entry.scope, kind: entry.kind,
      blocks: blocks.map(paintedBlock),
    },
  }), [entry, queryKey]));

  return (
    <article className="rounded-lg border border-border bg-card p-5">
      <h3 className={cn(text.heading, 'mb-3')}>{entry.title}</h3>
      <div className="space-y-3">
        {blocks.map((b, i) => <BlockView key={i} block={b} />)}
      </div>
    </article>
  );
}

function BlockView({ block }: { block: ContentBlock }) {
  if (block.kind === 'table') {
    const columns = (block.columns as string[]) ?? [];
    const rows = (block.rows as string[][]) ?? [];
    return (
      <div className="overflow-x-auto">
        <table className="w-full border-collapse text-left">
          <thead>
            <tr className="border-b border-border">
              {columns.map((c, i) => (
                <th key={i} className={cn(text.heading, 'px-3 py-2 align-top')}>{c}</th>
              ))}
            </tr>
          </thead>
          <tbody>
            {rows.map((row, ri) => (
              <tr key={ri} className="border-b border-border/50 align-top">
                {row.map((cell, ci) => (
                  <td key={ci} className={cn(text.body, 'px-3 py-2')}>{renderInline(cell)}</td>
                ))}
              </tr>
            ))}
          </tbody>
        </table>
      </div>
    );
  }

  if (block.kind === 'list') {
    const items = (block.items as string[]) ?? [];
    const ordered = !!block.ordered;
    const Tag = ordered ? 'ol' : 'ul';
    return (
      <Tag className={cn(text.body, ordered ? 'list-decimal' : 'list-disc', 'pl-5 space-y-1')}>
        {items.map((it, i) => <li key={i}>{renderInline(it)}</li>)}
      </Tag>
    );
  }

  // prose (default) — role hint (intro / verdict / callout) tunes emphasis.
  const txt = (block.text as string) ?? '';
  const role = block.role as string | undefined;
  const paras = txt.split(/\n\n+/);
  return (
    <div className={cn(text.body, role === 'verdict' && 'rounded-md bg-accent/30 p-3')}>
      {paras.map((p, i) => (
        <p key={i} className={i > 0 ? 'mt-2' : undefined}>{renderInline(p)}</p>
      ))}
    </div>
  );
}

// ── Painted-content export helpers ───────────────────────────────────
// The card paints entry.blocks; these mirror that paint into the capture
// scene. `stripInline` drops the `**`/`*` markers so the exported text
// matches what's on screen (the emphasis is rendered, not literal).
function stripInline(s: string): string {
  return s.replace(/\*\*([^*]+)\*\*/g, '$1').replace(/\*([^*]+)\*/g, '$1');
}

// Plain text — what the user reads, one block per paragraph.
function blockToText(b: ContentBlock): string {
  if (b.kind === 'table') {
    const cols = (b.columns as string[]) ?? [];
    const rows = (b.rows as string[][]) ?? [];
    return [cols.join(' | '), ...rows.map((r) => r.map(stripInline).join(' | '))].join('\n');
  }
  if (b.kind === 'list') {
    return ((b.items as string[]) ?? []).map((it) => `• ${stripInline(it)}`).join('\n');
  }
  return stripInline((b.text as string) ?? '');
}

// Markdown — preserves emphasis + renders tables as md tables for paste.
function blockToMarkdown(b: ContentBlock): string {
  if (b.kind === 'table') {
    const cols = (b.columns as string[]) ?? [];
    const rows = (b.rows as string[][]) ?? [];
    return [
      `| ${cols.join(' | ')} |`,
      `| ${cols.map(() => '---').join(' | ')} |`,
      ...rows.map((r) => `| ${r.join(' | ')} |`),
    ].join('\n');
  }
  if (b.kind === 'list') {
    const ordered = !!b.ordered;
    return ((b.items as string[]) ?? []).map((it, i) => `${ordered ? `${i + 1}.` : '-'} ${it}`).join('\n');
  }
  return (b.text as string) ?? '';
}

// Structured painted form for json — the blocks as rendered (emphasis
// stripped), so consumers can read table columns/rows and prose alike.
function paintedBlock(b: ContentBlock): Record<string, unknown> {
  if (b.kind === 'table') {
    return {
      kind: 'table',
      columns: (b.columns as string[]) ?? [],
      rows: ((b.rows as string[][]) ?? []).map((r) => r.map(stripInline)),
    };
  }
  if (b.kind === 'list') {
    return {
      kind: 'list',
      ordered: !!b.ordered,
      items: ((b.items as string[]) ?? []).map(stripInline),
    };
  }
  return { kind: 'prose', role: (b.role as string) ?? null, text: stripInline((b.text as string) ?? '') };
}

// Minimal INLINE markdown for prose / list items / table cells — `**bold**`
// and `*italic*`. Block-level structure (tables, lists) is NOT markdown —
// it's structured blocks (md is bad at tables; that's why TableBlock
// exists). The same `**`/`*` source converts to typst for the PDF/deck.
function renderInline(s: string): React.ReactNode {
  const tokens = s.split(/(\*\*[^*]+\*\*|\*[^*]+\*)/g);
  return tokens.map((tok, i) => {
    if (tok.startsWith('**') && tok.endsWith('**')) return <strong key={i}>{tok.slice(2, -2)}</strong>;
    if (tok.startsWith('*') && tok.endsWith('*')) return <em key={i}>{tok.slice(1, -1)}</em>;
    return <span key={i}>{tok}</span>;
  });
}
